import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import API from '../api/axios';
import { FaHome, FaShoppingBag, FaBell, FaUser, FaSignOutAlt, FaClock, FaStar, FaPaperPlane, FaCheckCircle, FaSpinner, FaTimesCircle, FaHourglassHalf, FaTicketAlt, FaPlus, FaShareAlt, FaFileInvoice, FaDownload, FaUpload, FaRedo, FaFileCsv, FaChartBar } from 'react-icons/fa';

// Countdown Timer Component
const CountdownTimer = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(deadline).getTime();
      const diff = end - now;
      if (diff <= 0) { setTimeLeft({ expired: true }); clearInterval(timer); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  if (timeLeft.expired) return <span style={{ color: 'var(--secondary)' }}>Deadline passed</span>;
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      {['days', 'hours', 'minutes', 'seconds'].map(unit => (
        <div key={unit} style={{ textAlign: 'center', padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '10px', minWidth: '55px' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--primary)' }}>{String(timeLeft[unit] || 0).padStart(2, '0')}</div>
          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{unit}</div>
        </div>
      ))}
    </div>
  );
};

const statusColors = {
  pending: '#ffc107', confirmed: '#00d2ff', in_progress: '#6c63ff', under_review: '#ff6584',
  revision: '#ff9800', completed: '#4caf50', cancelled: '#f44336', refunded: '#9c27b0'
};
const statusIcons = {
  pending: <FaHourglassHalf />, confirmed: <FaCheckCircle />, in_progress: <FaSpinner />,
  under_review: <FaClock />, completed: <FaCheckCircle />, cancelled: <FaTimesCircle />
};

// Orders List
const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { API.get('/orders/my-orders').then(r => setOrders(r.data)).catch(console.error); }, []);

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>My Orders</h1>
      {orders.length === 0 && <div className="admin-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>No orders yet. <Link to="/services" style={{ color: 'var(--primary)' }}>Browse Services</Link></div>}
      {orders.map(order => (
        <Link key={order._id} to={`/dashboard/orders/${order._id}`} style={{ textDecoration: 'none' }}>
          <div className="admin-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', cursor: 'pointer', transition: 'border-color 0.3s' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                <strong>{order.orderNumber}</strong>
                <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: `${statusColors[order.status]}20`, color: statusColors[order.status] }}>
                  {statusIcons[order.status]} {order.status.replace('_', ' ')}
                </span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{order.service?.title}</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{new Date(order.createdAt).toLocaleDateString()}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--primary)' }}>${order.totalAmount}</div>
              {order.status !== 'completed' && order.status !== 'cancelled' && (
                <div style={{ marginTop: '5px' }}><CountdownTimer deadline={order.deadline} /></div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

// Single Order Detail
const OrderDetail = () => {
  const [order, setOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const orderId = useLocation().pathname.split('/').pop();

  const fetchOrder = useCallback(async () => {
    try {
      const res = await API.get(`/orders/${orderId}`);
      setOrder(res.data);
      const chatRes = await API.get(`/chat/${orderId}`);
      setChatMessages(chatRes.data);
    } catch (err) { console.error(err); }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const res = await API.post('/chat', { orderId, message: newMessage });
      setChatMessages([...chatMessages, res.data]);
      setNewMessage('');
    } catch (err) { toast.error('Failed to send message'); }
  };

  const submitReview = async () => {
    try {
      await API.post('/reviews', { orderId, ...reviewForm });
      toast.success('Review submitted!');
      fetchOrder();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit review'); }
  };

  const downloadInvoice = async () => {
    try {
      const res = await API.get(`/invoice/${orderId}`);
      const w = window.open('', '_blank');
      w.document.write(res.data.html);
      w.document.close();
      setTimeout(() => w.print(), 500);
    } catch { toast.error('Failed to generate invoice'); }
  };

  const requestRevision = async () => {
    const notes = prompt('Describe what needs to be revised:');
    if (!notes) return;
    try {
      await API.put(`/orders/${orderId}/revision`, { notes });
      toast.success('Revision requested!');
      fetchOrder();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const files = [...(order.requirementFiles || []), { url: res.data.url, filename: res.data.filename, size: res.data.size }];
      await API.put(`/orders/${orderId}/files`, { requirementFiles: files });
      toast.success('File uploaded!');
      fetchOrder();
    } catch { toast.error('Upload failed'); }
  };

  const statusSteps = ['pending', 'confirmed', 'in_progress', 'under_review', 'completed'];
  const currentStepIdx = statusSteps.indexOf(order.status);

  if (!order) return <div style={{ color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
        <div>
          <h1 style={{ marginBottom: '5px' }}>Order {order.orderNumber}</h1>
          <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', background: `${statusColors[order.status]}20`, color: statusColors[order.status] }}>
            {order.status.replace('_', ' ')}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={downloadInvoice} className="btn btn-outline btn-small"><FaFileInvoice /> Invoice</button>
          {(order.status === 'under_review' || order.status === 'completed') && order.revisionCount < order.maxRevisions && (
            <button onClick={requestRevision} className="btn btn-small" style={{ background: '#ff9800', color: 'white' }}><FaRedo /> Request Revision ({order.revisionCount}/{order.maxRevisions})</button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {order.status !== 'cancelled' && (
        <div className="order-progress" style={{ margin: '20px 0' }}>
          {statusSteps.map((step, i) => (
            <div key={step} className={`progress-step ${i < currentStepIdx ? 'completed' : i === currentStepIdx ? 'active' : 'pending'}`}>
              <div className="step-circle">{i < currentStepIdx ? '✓' : i + 1}</div>
              <span className="step-label">{step.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', marginTop: '15px', flexWrap: 'wrap' }}>
        <div style={{ flex: '2', minWidth: '300px' }}>
          {/* Order Info */}
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Order Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', color: 'var(--text-secondary)' }}>
              <div><strong>Service:</strong> {order.service?.title}</div>
              <div><strong>Package:</strong> {order.packageType}</div>
              <div><strong>Amount:</strong> ${order.totalAmount}</div>
              <div><strong>Payment:</strong> {order.paymentMethod} ({order.paymentStatus})</div>
              <div><strong>Ordered:</strong> {new Date(order.createdAt).toLocaleDateString()}</div>
              <div><strong>Deadline:</strong> {new Date(order.deadline).toLocaleDateString()}</div>
            </div>
            {order.requirements && <div style={{ marginTop: '15px' }}><strong>Requirements:</strong><p style={{ color: 'var(--text-secondary)', marginTop: '5px' }}>{order.requirements}</p></div>}
            {order.deliverables && <div style={{ marginTop: '15px', padding: '15px', background: 'rgba(76,175,80,0.1)', borderRadius: '10px' }}><strong style={{ color: '#4caf50' }}>Deliverables:</strong><p style={{ color: 'var(--text-secondary)', marginTop: '5px' }}>{order.deliverables}</p></div>}
            {/* Deliverable Files */}
            {order.deliverableFiles?.length > 0 && (
              <div style={{ marginTop: '15px' }}>
                <strong style={{ color: '#4caf50' }}><FaDownload /> Delivered Files:</strong>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {order.deliverableFiles.map((f, i) => (
                    <a key={i} href={f.url} download={f.filename} style={{ padding: '8px 14px', background: 'rgba(76,175,80,0.1)', borderRadius: '10px', color: '#4caf50', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <FaDownload /> {f.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {/* Requirement Files Upload */}
            <div style={{ marginTop: '15px' }}>
              <strong>Your Files:</strong>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {order.requirementFiles?.map((f, i) => (
                  <span key={i} style={{ padding: '6px 12px', background: 'rgba(108,99,255,0.1)', borderRadius: '10px', fontSize: '0.8rem', color: 'var(--primary)' }}>{f.filename}</span>
                ))}
                <label style={{ padding: '6px 14px', background: 'var(--bg-dark)', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '5px', border: '1px dashed var(--border)' }}>
                  <FaUpload /> Upload File <input type="file" onChange={uploadFile} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          </div>

          {/* Countdown */}
          {order.status !== 'completed' && order.status !== 'cancelled' && (
            <div className="admin-card" style={{ textAlign: 'center' }}>
              <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}><FaClock /> Time Remaining</h3>
              <CountdownTimer deadline={order.deadline} />
            </div>
          )}

          {/* Chat */}
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Messages</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '15px' }}>
              {chatMessages.length === 0 && <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No messages yet</p>}
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ marginBottom: '10px', display: 'flex', justifyContent: msg.senderRole === 'client' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '70%', padding: '10px 15px', borderRadius: '15px',
                    background: msg.senderRole === 'client' ? 'var(--gradient-1)' : 'var(--bg-dark)', color: 'white' }}>
                    <p style={{ fontSize: '0.85rem' }}>{msg.message}</p>
                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{new Date(msg.createdAt).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a message..."
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                style={{ flex: 1, padding: '10px 15px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '25px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
              <button onClick={sendMessage} className="btn btn-primary btn-small" style={{ borderRadius: '25px' }}><FaPaperPlane /></button>
            </div>
          </div>

          {/* Review (only if completed) */}
          {order.status === 'completed' && (
            <div className="admin-card">
              <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}><FaStar /> Leave a Review</h3>
              <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star} onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: star <= reviewForm.rating ? '#ffc107' : 'var(--text-muted)' }}>
                    ★
                  </button>
                ))}
              </div>
              <div className="form-group">
                <textarea value={reviewForm.comment} onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder="Share your experience..." />
              </div>
              <button onClick={submitReview} className="btn btn-primary btn-small">Submit Review</button>
            </div>
          )}
        </div>

        {/* Right Summary */}
        <div style={{ flex: '1', minWidth: '250px' }}>
          <div className="admin-card" style={{ position: 'sticky', top: '20px' }}>
            <h4 style={{ marginBottom: '15px' }}>Payment Summary</h4>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span>Base</span><span>${order.amount}</span></div>
              {order.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#00d2ff' }}><span>Discount</span><span>-${order.discount}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                <span>Total</span><span style={{ color: 'var(--primary)' }}>${order.totalAmount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notifications
const NotificationsList = () => {
  const [notifications, setNotifications] = useState([]);
  useEffect(() => { API.get('/notifications').then(r => setNotifications(r.data.notifications)).catch(console.error); }, []);

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>Notifications</h1>
      {notifications.length === 0 && <div className="admin-card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No notifications</div>}
      {notifications.map(n => (
        <div key={n._id} className="admin-card" style={{ opacity: n.read ? 0.6 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div><strong>{n.title}</strong><p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{n.message}</p></div>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{new Date(n.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Support Tickets
const TicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'medium', message: '' });
  useEffect(() => { API.get('/tickets/my-tickets').then(r => setTickets(r.data)).catch(console.error); }, []);
  const statusColors = { open: '#ffc107', in_progress: '#6c63ff', waiting_client: '#ff6584', resolved: '#4caf50', closed: '#888' };

  const createTicket = async (e) => {
    e.preventDefault();
    try {
      await API.post('/tickets', form);
      toast.success('Ticket created!');
      setShowForm(false); setForm({ subject: '', category: 'general', priority: 'medium', message: '' });
      API.get('/tickets/my-tickets').then(r => setTickets(r.data));
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <div className="admin-header"><h1>Support Tickets</h1>
        <button className="btn btn-primary btn-small" onClick={() => setShowForm(!showForm)}><FaPlus /> New Ticket</button></div>
      {showForm && (
        <div className="admin-card">
          <form onSubmit={createTicket}>
            <div className="form-group"><label>Subject *</label><input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required /></div>
            <div style={{ display: 'flex', gap: '15px' }}>
              <div className="form-group" style={{ flex: 1 }}><label>Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ width: '100%', padding: '12px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }}>
                  <option value="general">General</option><option value="order_issue">Order Issue</option><option value="payment">Payment</option>
                  <option value="technical">Technical</option><option value="refund">Refund</option><option value="other">Other</option>
                </select></div>
              <div className="form-group" style={{ flex: 1 }}><label>Priority</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ width: '100%', padding: '12px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select></div>
            </div>
            <div className="form-group"><label>Message *</label><textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required placeholder="Describe your issue..." /></div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary btn-small">Submit Ticket</button>
              <button type="button" className="btn btn-outline btn-small" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {tickets.map(t => (
        <Link key={t._id} to={`/dashboard/tickets/${t._id}`} style={{ textDecoration: 'none' }}>
          <div className="admin-card" style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                  <strong>{t.ticketNumber}</strong>
                  <span style={{ padding: '2px 10px', borderRadius: '15px', fontSize: '0.7rem', background: `${statusColors[t.status]}20`, color: statusColors[t.status] }}>{t.status.replace('_', ' ')}</span>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', background: 'rgba(108,99,255,0.1)', color: 'var(--text-muted)' }}>{t.priority}</span>
                </div>
                <p style={{ color: 'var(--text-secondary)' }}>{t.subject}</p>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{new Date(t.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </Link>
      ))}
      {tickets.length === 0 && <div className="admin-card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No tickets yet</div>}
    </div>
  );
};

// Ticket Detail
const TicketDetail = () => {
  const [ticket, setTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const ticketId = useLocation().pathname.split('/').pop();

  const fetchTicket = useCallback(async () => {
    try { const r = await API.get(`/tickets/${ticketId}`); setTicket(r.data); } catch (e) { console.error(e); }
  }, [ticketId]);
  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const sendReply = async () => {
    if (!newMessage.trim()) return;
    try { await API.post(`/tickets/${ticketId}/message`, { message: newMessage }); setNewMessage(''); fetchTicket(); } catch { toast.error('Failed'); }
  };

  if (!ticket) return <div>Loading...</div>;

  return (
    <div>
      <h1>{ticket.ticketNumber} - {ticket.subject}</h1>
      <div style={{ display: 'flex', gap: '10px', margin: '10px 0 20px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
        <span>Status: {ticket.status}</span> | <span>Priority: {ticket.priority}</span> | <span>Category: {ticket.category}</span>
      </div>
      <div className="admin-card">
        <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '15px' }}>
          {ticket.messages?.map((msg, i) => (
            <div key={i} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.senderRole === 'client' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '70%', padding: '12px 16px', borderRadius: '15px', background: msg.senderRole === 'client' ? 'var(--gradient-1)' : 'var(--bg-dark)', color: 'white' }}>
                <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '4px' }}>{msg.senderRole === 'admin' ? 'Support' : 'You'}</p>
                <p style={{ fontSize: '0.9rem' }}>{msg.message}</p>
                <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>{new Date(msg.createdAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
        {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type reply..." onKeyDown={e => e.key === 'Enter' && sendReply()}
              style={{ flex: 1, padding: '10px 15px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '25px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
            <button onClick={sendReply} className="btn btn-primary btn-small" style={{ borderRadius: '25px' }}><FaPaperPlane /></button>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Home with Stats
const ClientHome = ({ userName }) => {
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, totalSpent: 0 });
  useEffect(() => { API.get('/orders/stats/my').then(r => setStats(r.data)).catch(() => {}); }, []);
  const exportCSV = () => { window.open(`${API.defaults.baseURL}/orders/export/csv`, '_blank'); };

  return (
    <div>
      <h1>Welcome back, {userName}!</h1>
      <p style={{ color: 'var(--text-secondary)', marginTop: '5px', marginBottom: '25px' }}>Here's your account overview</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
        {[{ val: stats.total, label: 'Total Orders', icon: <FaShoppingBag />, color: '#6c63ff' },
          { val: stats.pending, label: 'Active Orders', icon: <FaSpinner />, color: '#ffc107' },
          { val: stats.completed, label: 'Completed', icon: <FaCheckCircle />, color: '#4caf50' },
          { val: `$${stats.totalSpent}`, label: 'Total Spent', icon: <FaChartBar />, color: '#00d2ff' }
        ].map((c, i) => (
          <div key={i} className="admin-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', color: c.color, marginBottom: '8px' }}>{c.icon}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: c.color }}>{c.val}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{c.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
        <Link to="/dashboard/orders" className="admin-card" style={{ textAlign: 'center', textDecoration: 'none' }}><FaShoppingBag style={{ fontSize: '1.5rem', color: 'var(--primary)', marginBottom: '8px' }} /><h4>My Orders</h4></Link>
        <Link to="/services" className="admin-card" style={{ textAlign: 'center', textDecoration: 'none' }}><FaShoppingBag style={{ fontSize: '1.5rem', color: 'var(--secondary)', marginBottom: '8px' }} /><h4>Browse Services</h4></Link>
        <Link to="/dashboard/tickets" className="admin-card" style={{ textAlign: 'center', textDecoration: 'none' }}><FaTicketAlt style={{ fontSize: '1.5rem', color: '#ffc371', marginBottom: '8px' }} /><h4>Support</h4></Link>
        <div className="admin-card" style={{ textAlign: 'center', cursor: 'pointer' }} onClick={exportCSV}><FaFileCsv style={{ fontSize: '1.5rem', color: '#4caf50', marginBottom: '8px' }} /><h4>Export Orders</h4></div>
      </div>
    </div>
  );
};

// Referral
const ReferralSection = () => {
  const [data, setData] = useState({ code: '', referrals: [], totalEarned: 0 });
  useEffect(() => { API.get('/referrals/my-code').then(r => setData(r.data)).catch(console.error); }, []);
  const copyCode = () => { navigator.clipboard.writeText(`${window.location.origin}/register?ref=${data.code}`); toast.success('Referral link copied!'); };

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>Referral Program</h1>
      <div className="admin-card" style={{ textAlign: 'center' }}>
        <h3 style={{ color: 'var(--primary)', marginBottom: '10px' }}>Your Referral Code</h3>
        <div style={{ fontSize: '2rem', fontWeight: '700', letterSpacing: '5px', color: 'var(--accent)', marginBottom: '15px' }}>{data.code || 'Loading...'}</div>
        <button onClick={copyCode} className="btn btn-primary btn-small"><FaShareAlt /> Copy Referral Link</button>
        <p style={{ color: 'var(--text-muted)', marginTop: '15px', fontSize: '0.85rem' }}>Share your link and earn rewards when friends place orders!</p>
      </div>
      <div className="admin-card">
        <h3 style={{ marginBottom: '10px' }}>Total Earned: <span style={{ color: 'var(--primary)' }}>${data.totalEarned}</span></h3>
        <h4 style={{ marginBottom: '15px', color: 'var(--text-muted)' }}>Referrals ({data.referrals.length})</h4>
        {data.referrals.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            <span>{r.referred?.name || 'User'}</span><span style={{ color: r.status === 'rewarded' ? '#4caf50' : 'var(--text-muted)' }}>{r.status}</span>
          </div>
        ))}
        {data.referrals.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No referrals yet</p>}
      </div>
    </div>
  );
};

// Profile
const ProfileSection = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: '', phone: '', address: '' });
  useEffect(() => { API.get('/auth/me').then(r => setProfile(r.data)).catch(console.error); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try { await API.put('/auth/me', profile); toast.success('Profile updated!'); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>My Profile</h1>
      <div className="admin-card">
        <form onSubmit={handleSave}>
          <div className="form-group"><label>Name</label><input type="text" value={profile.name || ''} onChange={e => setProfile({ ...profile, name: e.target.value })} /></div>
          <div className="form-group"><label>Email</label><input type="email" value={profile.email || ''} disabled style={{ opacity: 0.6 }} /></div>
          <div className="form-group"><label>Phone</label><input type="text" value={profile.phone || ''} onChange={e => setProfile({ ...profile, phone: e.target.value })} /></div>
          <div className="form-group"><label>Address</label><input type="text" value={profile.address || ''} onChange={e => setProfile({ ...profile, address: e.target.value })} /></div>
          <button type="submit" className="btn btn-primary">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

// Main Client Dashboard
const ClientDashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--primary)' }}>Loading...</div>;
  if (!user) return null;

  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <h2>Dashboard</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '20px' }}>Welcome, {user.name}</p>
        <Link to="/dashboard" className={isActive('/dashboard')}><FaHome /> Overview</Link>
        <Link to="/dashboard/orders" className={isActive('/dashboard/orders')}><FaShoppingBag /> My Orders</Link>
        <Link to="/dashboard/tickets" className={isActive('/dashboard/tickets')}><FaTicketAlt /> Support Tickets</Link>
        <Link to="/dashboard/notifications" className={isActive('/dashboard/notifications')}><FaBell /> Notifications</Link>
        <Link to="/dashboard/referral" className={isActive('/dashboard/referral')}><FaShareAlt /> Referral</Link>
        <Link to="/dashboard/profile" className={isActive('/dashboard/profile')}><FaUser /> Profile</Link>
        <div style={{ borderTop: '1px solid var(--border)', margin: '15px 0' }} />
        <Link to="/services"><FaShoppingBag /> Browse Services</Link>
        <Link to="/"><FaHome /> Home</Link>
        <a href="#logout" onClick={e => { e.preventDefault(); logout(); navigate('/login'); }} style={{ color: '#ff4757', marginTop: '10px' }}><FaSignOutAlt /> Logout</a>
      </aside>
      <main className="admin-main">
        <Routes>
          <Route path="/" element={<ClientHome userName={user.name} />} />
          <Route path="/orders" element={<OrdersList />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/tickets" element={<TicketsList />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/notifications" element={<NotificationsList />} />
          <Route path="/referral" element={<ReferralSection />} />
          <Route path="/profile" element={<ProfileSection />} />
        </Routes>
      </main>
    </div>
  );
};

export default ClientDashboard;
